import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	Drawer,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
}                                    from '@mui/material'
import {
	Add,
	ArrowBack,
	Close,
	ContentCopy,
	Delete,
	OpenInNew,
}                                    from '@mui/icons-material'
import { useNavigate }               from 'react-router-dom'
import { doDelete, doGet, doPut }    from '../../util/do-fetch'
import HoverTableRow                 from '../HoverTableRow'
import WelcomeMessageQr              from './WelcomeMessageQr'

/*
 * Welcome message editor (085, 092, 093).
 *
 * A "message" is the heading + subheading shown on /login, stored as config rows under the
 * LOGIN_MSG_ prefix — see service/welcome-message.js for the key convention.
 *
 * A message reaches a student one of two ways, and that choice is the spine of this screen:
 *
 *   URL PATTERN (the default) — the message is stored with a path, and any sign-in headed
 *     somewhere under it shows the message on its own. Nothing to share, so no link and no QR
 *     code: the campaign links already exist and simply start working.
 *
 *   LINK TOKEN — no stored path. The message appears only on a link carrying ?msg=TOKEN, which
 *     this screen builds, along with the post-login destination and a QR code for it.
 *
 * Both live in one drawer. They were previously split between a drawer for the copy and a panel
 * under the table for the link, which meant editing one message in two places.
 */

// the fallback message every link falls back to; it cannot be renamed or deleted
const DEFAULT_TOKEN = 'DEFAULT'

// where a link can send someone after sign-in; free text is allowed for anything else
const DESTINATION_PRESETS = [
	{ label: 'PrepKits (default)', path: '/prepkit' },
	{ label: 'Exam catalogue',     path: '/prep' },
]

const TOKEN_PATTERN = /^[A-Za-z0-9-]{1,64}$/

const MODE_PATTERN = 'pattern'
const MODE_TOKEN = 'token'

/*
 * Every message is stored as LOGIN_MSG_<TOKEN>, so a pattern message needs a token too -- but it
 * is an internal key there, never something anyone types or shares, so it is derived from the
 * pattern rather than asked for. '/try/ai-certifications' becomes 'TRY-AI-CERTIFICATIONS'.
 */
const tokenFromPattern = (pattern, taken = []) => {
	const base = String(pattern || '')
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 60) || 'MESSAGE'

	if (!taken.includes(base)) return base

	// a second message on the same path is a mistake, but colliding keys would silently
	// overwrite the first one, so suffix instead
	let n = 2
	while (taken.includes(`${base}-${n}`)) n++
	return `${base}-${n}`
}

const WelcomeMessages = () => {
	const navigate = useNavigate()

	const [messages, setMessages] = useState(null)
	const [error, setError]       = useState(null)
	const [saving, setSaving]     = useState(false)
	const [copied, setCopied]     = useState(false)

	// the message open in the drawer: {token, title, subtitle, mode, pattern, destination, isNew}
	const [draft, setDraft] = useState(null)

	const fetchMessages = useCallback(async () => {
		try {
			const response = await doGet('/admin/welcome-messages')
			if (!response.ok) throw new Error(await response.text())
			setMessages(await response.json())
			setError(null)
		}
		catch (err) {
			console.error(err)
			setError('Could not load welcome messages.')
		}
	}, [])

	useEffect(() => { fetchMessages() }, [fetchMessages])

	/*
	 * A stored path IS pattern matching -- there is no separate flag that could disagree with it.
	 * New messages open in pattern mode: matching a URL is the common case, and a message nobody
	 * can reach without a hand-built link is the exception.
	 */
	const openMessage = (message) => {
		setCopied(false)
		setDraft({
			token: message.token,
			originalToken: message.token,
			title: message.title || '',
			subtitle: message.subtitle || '',
			mode: message.path ? MODE_PATTERN : MODE_TOKEN,
			pattern: message.path || '',
			destination: message.path || DESTINATION_PRESETS[0].path,
			isNew: false,
		})
	}

	const openNew = () => {
		setCopied(false)
		setDraft({
			token: '',
			originalToken: null,
			title: '',
			subtitle: '',
			mode: MODE_PATTERN,
			pattern: '',
			destination: DESTINATION_PRESETS[0].path,
			isNew: true,
		})
	}

	const update = (changes) => setDraft(current => ({ ...current, ...changes }))

	/*
	 * The link an admin shares, for token messages only. Built against the app origin rather than
	 * the API's, since /login is a front-end route.
	 */
	const link = useMemo(() => {
		if (!draft || draft.mode !== MODE_TOKEN || !draft.token) return null
		const origin = process.env.REACT_APP_SITE_URL || window.location.origin
		const next = encodeURIComponent(draft.destination || '/prepkit')
		return `${origin}/login?next=${next}&msg=${draft.token}`
	}, [draft])

	const handleCopy = async () => {
		if (!link) return
		try {
			await navigator.clipboard.writeText(link)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}
		catch (err) {
			console.error('Clipboard write failed', err)
		}
	}

	const handleSave = async () => {
		if (!draft) return
		setSaving(true)
		setError(null)
		try {
			// a pattern message keeps whatever key it already had, and a new one gets a derived key
			const token = (draft.mode === MODE_PATTERN && !draft.originalToken
				? tokenFromPattern(draft.pattern, (messages || []).map(m => m.token))
				: draft.token
			).toUpperCase()

			// the mode decides whether a path is stored at all; that is the whole difference
			const options = {
				body: JSON.stringify({
					title: draft.title,
					subtitle: draft.subtitle,
					path: draft.mode === MODE_PATTERN ? draft.pattern : '',
				}),
			}
			const response = await doPut(`/admin/welcome-messages/${token}`, options)
			if (!response.ok) throw new Error(await response.text())

			/*
			 * There is no rename on the API -- a token is the config key -- so a rename is written
			 * as the new message then a delete of the old. In that order: a failure between the two
			 * leaves a duplicate, which is recoverable, rather than nothing at all.
			 */
			if (draft.originalToken && draft.originalToken !== token) {
				const cleanup = await doDelete(`/admin/welcome-messages/${draft.originalToken}`)
				if (!cleanup.ok) console.error('Renamed the message but could not remove', draft.originalToken)
			}

			await fetchMessages()
			setDraft(null)
		}
		catch (err) {
			console.error(err)
			setError(err.message || 'Could not save the message.')
		}
		finally {
			setSaving(false)
		}
	}

	const handleDelete = async (token) => {
		if (!window.confirm(`Delete the "${token}" welcome message? Links using ?msg=${token} will fall back to the default.`)) return
		try {
			const response = await doDelete(`/admin/welcome-messages/${token}`)
			if (!response.ok) throw new Error(await response.text())
			if (draft && draft.token === token) setDraft(null)
			await fetchMessages()
		}
		catch (err) {
			console.error(err)
			setError(err.message || 'Could not delete the message.')
		}
	}

	const tokenError = draft && draft.token && !TOKEN_PATTERN.test(draft.token)
		? 'Letters, numbers and hyphens only (no underscores).'
		: null

	const patternError = draft && draft.mode === MODE_PATTERN && draft.pattern && !draft.pattern.startsWith('/')
		? 'Must start with /'
		: null

	// renaming an existing message rewrites its key, which is what breaks links that use the old one
	const isRenaming = Boolean(draft && draft.originalToken && draft.token.toUpperCase() !== draft.originalToken)

	const canSave = draft
		&& draft.title.trim()
		&& !patternError
		&& (draft.mode === MODE_PATTERN
			? draft.pattern.trim()
			: draft.token.trim() && !tokenError)

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Box display="flex" flexDirection="row" alignItems="center" mb={1}>
				<IconButton onClick={() => navigate('/admin')}><ArrowBack /></IconButton>
				<Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Welcome Messages</Typography>
				<Button variant="contained" startIcon={<Add />} onClick={openNew}>
					New message
				</Button>
			</Box>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				The heading and subheading shown on the sign-in screen. A message is shown either by
				matching the URL someone is heading for, or by a link carrying its token.
			</Typography>

			{error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

			{!messages && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}

			{messages && (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell><b>Token / URL</b></TableCell>
								<TableCell><b>Heading</b></TableCell>
								<TableCell><b>Shown when</b></TableCell>
								<TableCell align="right"/>
							</TableRow>
						</TableHead>
						<TableBody>
							{messages.length === 0 && (
								<TableRow>
									<TableCell colSpan={4}>
										<Typography variant="body2" color="text.secondary" py={2}>
											No messages yet. The sign-in screen uses its built-in copy.
										</Typography>
									</TableCell>
								</TableRow>
							)}

							{messages.map(message => (
								<HoverTableRow
									key={message.token}
									hover
									onClick={() => openMessage(message)}
									sx={{ cursor: 'pointer' }}
								>
									{/* a pattern message is known by its URL; its token is an internal key */}
									<TableCell sx={{ fontFamily: 'monospace' }}>
										{message.path || message.token}
										{message.token === DEFAULT_TOKEN && (
											<Chip size="small" label="fallback" sx={{ ml: 1 }} />
										)}
									</TableCell>
									<TableCell>
										{message.title}
										{!message.subtitle && (
											<Typography variant="caption" color="text.secondary" component="div">
												uses the default subheading
											</Typography>
										)}
									</TableCell>
									{/* the one thing worth seeing at a glance: how anyone reaches this message */}
									<TableCell sx={{ fontFamily: 'monospace', fontSize: '.8rem' }}>
										{message.path
											? <>URL starts with {message.path}</>
											: <Box component="span" sx={{ opacity: .7 }}>?msg={message.token}</Box>}
									</TableCell>
									<TableCell align="right">
										<Tooltip title={message.token === DEFAULT_TOKEN ? 'The fallback message cannot be deleted' : 'Delete'}>
											<span>
												<IconButton
													size="small"
													disabled={message.token === DEFAULT_TOKEN}
													onClick={(e) => { e.stopPropagation(); handleDelete(message.token) }}
												>
													<Delete fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</HoverTableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{/*
			  * One drawer for the whole message: copy, how it is reached, and -- for token messages
			  * -- the link and QR code. The list stays visible behind it.
			  */}
			<Drawer
				anchor="right"
				open={Boolean(draft)}
				onClose={() => setDraft(null)}
				PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
			>
				{draft && (
					<>
						<Box display="flex" alignItems="center" gap={1} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
							<Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
								{draft.isNew
									? 'New welcome message'
									: (draft.mode === MODE_PATTERN ? (draft.pattern || draft.token) : draft.token)}
							</Typography>
							<IconButton onClick={() => setDraft(null)}><Close /></IconButton>
						</Box>

						<Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
							{draft.token === DEFAULT_TOKEN && (
								<Alert severity="info" sx={{ mb: 2 }}>
									The fallback message, shown whenever nothing else matches.
								</Alert>
							)}

							<TextField
								fullWidth
								label="Heading"
								value={draft.title}
								onChange={(e) => update({ title: e.target.value })}
								sx={{ mt: 1, mb: 2 }}
							/>

							<TextField
								fullWidth
								multiline
								minRows={2}
								label="Subheading"
								value={draft.subtitle}
								onChange={(e) => update({ subtitle: e.target.value })}
								helperText="Leave blank to use the default subheading"
								sx={{ mb: 3 }}
							/>

							<Divider sx={{ mb: 2 }} />

							<Typography variant="subtitle2" sx={{ mb: 1 }}>How is this message shown?</Typography>
							<ToggleButtonGroup
								exclusive
								fullWidth
								size="small"
								value={draft.mode}
								onChange={(e, mode) => mode && update({ mode })}
								sx={{ mb: 2 }}
							>
								<ToggleButton value={MODE_PATTERN} sx={{ textTransform: 'none' }}>
									Match a URL
								</ToggleButton>
								<ToggleButton value={MODE_TOKEN} sx={{ textTransform: 'none' }}>
									Share a link
								</ToggleButton>
							</ToggleButtonGroup>

							{draft.mode === MODE_PATTERN ? (
								<>
									<TextField
										fullWidth
										label="URL pattern"
										placeholder="/try"
										value={draft.pattern}
										onChange={(e) => update({ pattern: e.target.value })}
										error={Boolean(patternError)}
										helperText={patternError || 'A path within the app, starting with /'}
										sx={{ mb: 1 }}
									/>
									<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
										Anyone signing in on the way to a URL starting with{' '}
										<Box component="span" sx={{ fontFamily: 'monospace' }}>{draft.pattern || '/…'}</Box>{' '}
										sees this message automatically — existing links need nothing added to them.
										A message with a more specific pattern wins over this one.
									</Typography>
								</>
							) : (
								<>
									{/* the token is the shareable identity of a link message, so it is
									    edited here rather than up top -- a pattern message never shows it */}
									<TextField
										fullWidth
										label="Token"
										value={draft.token}
										onChange={(e) => update({ token: e.target.value.toUpperCase() })}
										error={Boolean(tokenError)}
										helperText={tokenError || 'Appears in the link as ?msg=TOKEN'}
										sx={{ mb: isRenaming ? 1 : 2 }}
									/>

									{isRenaming && (
										<Alert severity="warning" sx={{ mb: 2 }}>
											Renaming to <b>{draft.token.toUpperCase()}</b> will break any link already
											shared as{' '}
											<Box component="span" sx={{ fontFamily: 'monospace' }}>?msg={draft.originalToken}</Box>{' '}
											— those links fall back to the default message. QR codes already printed
											carry the old token too.
										</Alert>
									)}

									{/* the destination only exists to be baked into the link */}
									<TextField
										select
										fullWidth
										label="After sign-in, send them to"
										value={DESTINATION_PRESETS.some(p => p.path === draft.destination) ? draft.destination : '__custom'}
										onChange={(e) => {
											if (e.target.value !== '__custom') update({ destination: e.target.value })
										}}
										sx={{ mb: 2 }}
									>
										{DESTINATION_PRESETS.map(preset => (
											<MenuItem key={preset.path} value={preset.path}>
												{preset.label} <Box component="span" sx={{ ml: 1, opacity: .6 }}>{preset.path}</Box>
											</MenuItem>
										))}
										<MenuItem value="__custom">Custom path&hellip;</MenuItem>
									</TextField>

									<TextField
										fullWidth
										label="Destination path"
										value={draft.destination}
										onChange={(e) => update({ destination: e.target.value })}
										helperText="A path within the app, starting with /"
										sx={{ mb: 2 }}
									/>

									<Typography variant="caption" color="text.secondary">Shareable link</Typography>
									<Box sx={{
										mt: .5, p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '.8rem',
										backgroundColor: 'action.hover', wordBreak: 'break-all',
									}}>
										{link}
									</Box>

									<Box display="flex" gap={1} mt={1.5} mb={2}>
										<Button size="small" variant="contained" startIcon={<ContentCopy />} onClick={handleCopy}>
											{copied ? 'Copied' : 'Copy link'}
										</Button>
										<Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(link, '_blank', 'noopener')}>
											Preview
										</Button>
									</Box>

									{/* the QR code encodes the link above, so it only exists in this mode */}
									<WelcomeMessageQr url={link} token={draft.token} />
								</>
							)}
						</Box>

						<Box display="flex" justifyContent="flex-end" gap={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
							<Button onClick={() => setDraft(null)}>Cancel</Button>
							<Button variant="contained" disabled={!canSave || saving} onClick={handleSave}>
								{saving ? 'Saving…' : 'Save'}
							</Button>
						</Box>
					</>
				)}
			</Drawer>
		</Container>
	)
}

export default WelcomeMessages
